import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { firstValueFrom } from 'rxjs';

import { ItemImagemConsultaDTO, ItemImagemDTO, ItemImagensLoteDTO } from '../../../models/produtos/item-imagem.model';
import { ItemImagemService } from '../../../services/produtos/item-imagem.service';
import { getImagemSecundariaDoItem } from '../../utils/item-imagem.util';

/**
 * Componente de CADASTRO de imagens secundarias do item.
 *
 * Responsavel por:
 *  - Listar as imagens ja salvas (GET ao receber `itemId`).
 *  - Permitir upload de novas imagens (multi-select, ate {{LIMITE_TOTAL}} no total).
 *  - Validar client-side: formato, tamanho (<= 2 MB) e quantidade.
 *  - Enviar em LOTES de {{IMAGENS_POR_LOTE}} (gargalo nginx producao 5 MB).
 *  - Mostrar progresso e tratar falhas (incluindo fallback 413 -> sublotes de 1).
 *  - Lightbox embutido para ampliar.
 *  - Apos POST OK, invalidar cache do service para garantir consistencia entre telas.
 */
@Component({
  selector: 'app-item-imagens-secundarias',
  templateUrl: './item-imagens-secundarias.component.html',
  styleUrls: ['./item-imagens-secundarias.component.scss'],
  providers: [MessageService],
})
export class ItemImagensSecundariasComponent implements OnInit, OnChanges, OnDestroy {
  @Input() itemId: number | undefined;
  @Input() disabled = false;

  @ViewChild('fileUploader') fileUploader: FileUpload | undefined;

  /** Limite TOTAL de imagens secundarias por item (regra do backend). */
  readonly LIMITE_TOTAL = 10;
  /** Imagens enviadas por lote (gargalo nginx producao client_max_body_size 5MB). */
  readonly IMAGENS_POR_LOTE = 2;
  /** Tamanho maximo por imagem em bytes (2 MB). */
  readonly TAMANHO_MAX_BYTES = 2 * 1024 * 1024;
  /** Formatos de imagem permitidos. */
  readonly MIME_PERMITIDOS = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

  // === Estado UI ===
  carregando = false;
  enviando = false;
  erroCarregamento = false;

  /** Imagens ja salvas no backend (GET). */
  imagens: ItemImagemConsultaDTO[] = [];

  /** Progresso do envio em lote: { atual, total }. */
  progressoEnvio: { atual: number; total: number } | null = null;

  /** Id da imagem em processo de remocao (mostra spinner no thumb e bloqueia). */
  removendoId: number | null = null;

  // === Lightbox embutido ===
  lightboxAberto = false;
  lightboxUrl: string | null = null;
  lightboxAlt = '';
  private elementoFocadoAntesLightbox: HTMLElement | null = null;

  // === idEmpresa ===
  private idEmpresa: string = '';

  constructor(
    private itemImagemService: ItemImagemService,
    private translate: TranslateService,
    private messageService: MessageService,
    private elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.idEmpresa = sessionStorage.getItem('id') || '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itemId']) {
      const valorAtual = changes['itemId'].currentValue;
      const valorAnterior = changes['itemId'].previousValue;
      if (valorAtual !== valorAnterior) {
        this.imagens = [];
        this.erroCarregamento = false;
        if (valorAtual !== undefined && valorAtual !== null) {
          this.carregarImagens();
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.fecharLightbox();
  }

  // ===== Carregamento =====

  carregarImagens(): void {
    if (this.itemId === undefined || this.itemId === null) return;
    this.carregando = true;
    this.erroCarregamento = false;
    this.itemImagemService.buscarImagensSecundarias(this.itemId).subscribe({
      next: (lista) => {
        this.imagens = [...lista];
        this.carregando = false;
      },
      error: () => {
        this.carregando = false;
        this.erroCarregamento = true;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('item_imagens_secundarias.erros.erro_carregar_galeria'),
        });
      },
    });
  }

  // ===== Upload =====

  /**
   * Handler do p-fileUpload (uploader customizado).
   * Recebe os arquivos selecionados, valida e envia em lotes.
   */
  onSelectArquivos(event: { files: File[]; currentFiles?: File[] }): void {
    if (this.disabled || this.enviando || this.itemId === undefined) return;

    const arquivos = event.files || [];
    if (arquivos.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('item_imagens_secundarias.erros.envio_invalido'),
      });
      this.resetUploader();
      return;
    }

    // 1) Validar quantidade total
    const totalAposEnvio = this.imagens.length + arquivos.length;
    if (totalAposEnvio > this.LIMITE_TOTAL) {
      const podeAdicionar = Math.max(0, this.LIMITE_TOTAL - this.imagens.length);
      if (podeAdicionar === 0) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('item_imagens_secundarias.erros.limite_atingido'),
        });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('item_imagens_secundarias.erros.so_pode_adicionar_n', {
            n: podeAdicionar,
            ja: this.imagens.length,
            total: this.LIMITE_TOTAL,
          }),
        });
      }
      this.resetUploader();
      return;
    }

    // 2) Validar formato e tamanho
    for (const arquivo of arquivos) {
      if (!this.MIME_PERMITIDOS.includes(arquivo.type)) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('item_imagens_secundarias.erros.formato_invalido'),
        });
        this.resetUploader();
        return;
      }
      if (arquivo.size > this.TAMANHO_MAX_BYTES) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('item_imagens_secundarias.erros.imagem_muito_grande'),
        });
        this.resetUploader();
        return;
      }
    }

    // 3) Converter para base64 e enviar
    this.enviar(arquivos);
  }

  private async enviar(arquivos: File[]): Promise<void> {
    if (this.itemId === undefined) return;

    this.enviando = true;
    this.progressoEnvio = { atual: 0, total: arquivos.length };

    try {
      const base64List = await Promise.all(arquivos.map((f) => this.arquivoParaBase64(f)));
      await this.enviarEmLotes(base64List);

      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('item_imagens_secundarias.sucesso_envio'),
      });
    } catch (erro) {
      const enviadasComSucesso = this.progressoEnvio?.atual ?? 0;
      if (enviadasComSucesso > 0) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('item_imagens_secundarias.envio_parcial'),
        });
      } else {
        this.tratarErroEnvio(erro);
      }
    } finally {
      this.enviando = false;
      this.progressoEnvio = null;
      this.resetUploader();
    }
  }

  /**
   * Envia em lotes sequenciais de `IMAGENS_POR_LOTE` imagens.
   * Em caso de 413 (payload grande), reparticiona o lote em sublotes de 1.
   */
  private async enviarEmLotes(base64List: string[]): Promise<void> {
    if (this.itemId === undefined) return;

    for (let i = 0; i < base64List.length; i += this.IMAGENS_POR_LOTE) {
      const lote = base64List.slice(i, i + this.IMAGENS_POR_LOTE);
      try {
        const novas = await this.enviarLote(lote);
        this.imagens.push(...novas);
        this.itemImagemService.invalidarCache(this.itemId);
        this.progressoEnvio = {
          atual: Math.min(i + lote.length, base64List.length),
          total: base64List.length,
        };
      } catch (erro: unknown) {
        // Fallback 413: reparticionar em sublotes de 1
        if (this.ehErro413(erro) && lote.length > 1) {
          for (let j = 0; j < lote.length; j++) {
            const sublote = [lote[j]];
            const novas = await this.enviarLote(sublote);
            this.imagens.push(...novas);
            this.itemImagemService.invalidarCache(this.itemId);
            this.progressoEnvio = {
              atual: Math.min(i + j + 1, base64List.length),
              total: base64List.length,
            };
          }
        } else {
          throw erro;
        }
      }
    }
  }

  private async enviarLote(base64Lote: string[]): Promise<ItemImagemConsultaDTO[]> {
    if (this.itemId === undefined) {
      throw new Error('itemId indefinido');
    }
    const dto: ItemImagensLoteDTO = {
      imagens: base64Lote.map<ItemImagemDTO>((b64) => ({ imagemBase64: b64 })),
    };
    return await firstValueFrom(this.itemImagemService.adicionarImagens(this.itemId, dto));
  }

  private arquivoParaBase64(arquivo: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      // `readAsDataURL` retorna `data:image/...;base64,<conteudo>`. O backend
      // (ImagemService.decoderBase64imagem) decodifica a string SEM remover o
      // prefixo data URL, entao precisamos enviar apenas o conteudo base64 puro
      // — mesmo padrao usado no upload da imagem principal (stripDataUrlPrefix).
      reader.onload = () => resolve(this.removerPrefixoDataUrl(String(reader.result || '')));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(arquivo);
    });
  }

  /**
   * Remove o prefixo data URL (`data:image/...;base64,`) deixando apenas o
   * conteudo base64 puro que o backend espera para decodificar.
   */
  private removerPrefixoDataUrl(dataUrl: string): string {
    const idx = dataUrl.indexOf('base64,');
    return idx >= 0 ? dataUrl.substring(idx + 7) : dataUrl;
  }

  // ===== Erros =====

  private ehErro413(erro: unknown): boolean {
    const e = erro as { status?: number };
    return !!e && e.status === 413;
  }

  private tratarErroEnvio(erro: unknown): void {
    const e = erro as { status?: number; error?: { message?: string } };
    const status = e?.status;
    const mensagemBackend = e?.error?.message;

    let chave = 'item_imagens_secundarias.erros.erro_inesperado';
    if (status === 400) chave = 'item_imagens_secundarias.erros.envio_invalido';
    else if (status === 401 || status === 403) chave = 'item_imagens_secundarias.erros.sem_permissao';
    else if (status === 404) chave = 'item_imagens_secundarias.erros.item_nao_encontrado';
    else if (status === 413) chave = 'item_imagens_secundarias.erros.payload_grande';
    else if (status === 422) chave = 'item_imagens_secundarias.erros.regra_de_negocio_violada';

    this.messageService.add({
      severity: 'error',
      summary: mensagemBackend || this.translate.instant(chave),
    });
  }

  private resetUploader(): void {
    if (this.fileUploader) {
      this.fileUploader.clear();
    }
  }

  // ===== Remocao individual =====

  /**
   * Remove uma unica imagem secundaria. `stopPropagation` evita que o clique
   * no botao abra o lightbox do thumb. Atualiza a lista localmente e invalida
   * o cache do service apos sucesso.
   */
  removerImagem(imagem: ItemImagemConsultaDTO, event: Event): void {
    event.stopPropagation();
    if (this.disabled || this.enviando || this.removendoId !== null || this.itemId === undefined) {
      return;
    }
    this.removendoId = imagem.id;
    this.itemImagemService.removerImagem(this.itemId, imagem.id).subscribe({
      next: () => {
        this.imagens = this.imagens.filter((i) => i.id !== imagem.id);
        if (this.itemId !== undefined) {
          this.itemImagemService.invalidarCache(this.itemId);
        }
        this.removendoId = null;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('item_imagens_secundarias.sucesso_remocao'),
        });
      },
      error: (erro) => {
        this.removendoId = null;
        this.tratarErroEnvio(erro);
      },
    });
  }

  // ===== Lightbox =====

  abrirLightbox(imagem: ItemImagemConsultaDTO, indice: number): void {
    if (this.enviando) return;
    this.elementoFocadoAntesLightbox = (document.activeElement as HTMLElement) || null;
    this.lightboxUrl = this.urlDaImagem(imagem);
    this.lightboxAlt = this.translate.instant('item_imagens_secundarias.alt_foto', { numero: indice + 1 });
    this.lightboxAberto = true;
  }

  fecharLightbox(): void {
    this.lightboxAberto = false;
    this.lightboxUrl = null;
    this.lightboxAlt = '';
    if (this.elementoFocadoAntesLightbox && typeof this.elementoFocadoAntesLightbox.focus === 'function') {
      try {
        this.elementoFocadoAntesLightbox.focus();
      } catch {
        /* ignora */
      }
    }
    this.elementoFocadoAntesLightbox = null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.lightboxAberto) this.fecharLightbox();
  }

  // ===== Helpers de template =====

  urlDaImagem(imagem: ItemImagemConsultaDTO): string {
    return getImagemSecundariaDoItem(this.idEmpresa, imagem.imagem);
  }

  altDaImagem(indice: number): string {
    return this.translate.instant('item_imagens_secundarias.alt_foto', { numero: indice + 1 });
  }

  podeAdicionarMais(): boolean {
    return !this.disabled && !this.enviando && this.imagens.length < this.LIMITE_TOTAL;
  }

  textoContador(): string {
    return this.translate.instant('item_imagens_secundarias.contador', {
      quantidade: this.imagens.length,
      limite: this.LIMITE_TOTAL,
    });
  }
}
